import { useTranslation } from 'react-i18next';
import { useEffect, useRef, useState } from 'react';
import Hls from 'hls.js';

export default function HeroSection() {
  const { t } = useTranslation();
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const hlsRef = useRef<Hls | null>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => setIsVisible(entry.isIntersecting),
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;

    const src = '/media/hls/index.m3u8';

    // Quick path / MIME sanity check
    fetch(src)
      .then(r => {
        console.log('[HLS] fetch', src, r.status, r.headers.get('content-type'));
        if (!r.ok) console.error('[HLS] playlist not reachable (check public/ path & filename).');
      })
      .catch(err => console.error('[HLS] fetch failed:', err));

    const preventContextMenu = (e: Event) => e.preventDefault();
    v.addEventListener('contextmenu', preventContextMenu);

    if (v.canPlayType('application/vnd.apple.mpegURL')) {
      // Safari native HLS
      v.src = src;
    } else if (Hls.isSupported()) {
      const hls = new Hls();
      hlsRef.current = hls;

      // Log errors from hls.js (segment 404, CORS, parse issues, etc)
      hls.on(Hls.Events.ERROR, (_evt, data) => {
        console.error('[HLS] error', data.type, data.details, data);
      });

      hls.loadSource(src);
      hls.attachMedia(v);
    } else {
      console.warn('[HLS] Not supported in this browser. Consider an MP4 fallback.');
      // v.src = '/media/fallback.mp4';
    }

    const onEnded = () => {
      v.currentTime = 0;
      v.play().catch(() => {});
    };
    v.addEventListener('ended', onEnded);

    return () => {
      v.removeEventListener('ended', onEnded);
      v.removeEventListener('contextmenu', preventContextMenu);
      if (hlsRef.current) {
        hlsRef.current.destroy();
        hlsRef.current = null;
      }
    };
  }, []);

  const scrollToAbout = () => {
    const element = document.getElementById('about');
    if (!element) return;
    const navHeight = 64;
    const elementPosition = element.getBoundingClientRect().top;
    const offsetPosition = elementPosition + window.pageYOffset - navHeight;
    window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
  };

  return (
    <section
      ref={sectionRef}
      id="hero"
      className={`relative h-screen flex items-center justify-center overflow-hidden transition-all duration-1000 ${
        isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
      }`}
    >
      <div className="absolute inset-0 z-0">
        <video
          ref={videoRef}
          autoPlay
          muted
          playsInline
          controlsList="nodownload"
          className="w-full h-full object-cover opacity-40"
        />
        <div className="absolute inset-0 bg-black/30" />
      </div>

      <div className="relative z-10 text-center text-white max-w-4xl mx-auto px-6">
        <h1 className="text-5xl md:text-6xl font-bold mb-6 leading-tight">
          {t('hero.title')}
        </h1>
        <p className="text-xl md:text-2xl mb-8 text-gray-200 leading-relaxed">
          {t('hero.subtitle')}
        </p>
        <button
          onClick={scrollToAbout}
          className="bg-blue-500 hover:bg-blue-600 text-white px-8 py-4 rounded-lg text-lg font-semibold transition-all duration-300 cursor-pointer whitespace-nowrap inline-flex items-center gap-3 hover:shadow-lg transform hover:-translate-y-1"
        >
         {t('hero.btn')}
          <i className="ri-arrow-down-line w-5 h-5 flex items-center justify-center"></i>
        </button>
      </div>
    </section>
  );
}
